import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDateTime } from "@/lib/api";
import { KycBadge } from "@/components/KycForm";

const FILTERS = ["Pending", "Rejected", "Approved", "All"];

export const AdminKyc = ({ role }) => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("Pending");
  const [rules, setRules] = useState(null);
  useEffect(() => { api.get("/admin/rules").then((r) => setRules(r.data)).catch(() => {}); }, []);
  const setRule = async (k, v) => {
    try { const { data } = await api.put("/admin/rules", { ...rules, [k]: v }); setRules(data); toast.success("Rule saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const load = () => api.get("/admin/kyc").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const decide = async (r, decision) => {
    const reason = decision === "Rejected" ? window.prompt(`Why is ${r.name}'s KYC not approved? (they will see this)`, "") : "";
    if (reason === null) return;
    try { await api.post(`/admin/kyc/${r.kind}/${r.id}`, { decision, reason }); toast.success(`KYC ${decision.toLowerCase()}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((r) => filter === "All" || r.kyc.status === filter);
  return (
    <div className="max-w-5xl space-y-4">
      {rules && (
        <div className="space-y-2 rounded-2xl border-2 border-[#2E9E6B] bg-white p-4" data-testid="kyc-rules">
          <p className="font-bold text-[#0D3B2B]">Verification rules {role !== "admin" && <span className="text-xs font-normal text-[#7B968B]">(only the admin can change these)</span>}</p>
          {[["business_kyc_required", "Business customers need approved KYC before a pickup can be confirmed or completed"],
            ["partner_verification_required", "Partners need approved KYC + police verification before they can be assigned pickups"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 text-sm"><input type="checkbox" className="h-5 w-5" checked={rules[k]} disabled={role !== "admin"}
              onChange={(e) => setRule(k, e.target.checked)} data-testid={`rule-${k}`} />{l}</label>
          ))}
          <label className="flex flex-wrap items-center gap-3 text-sm">Partners can change the rate per kg (if the customer negotiates) by up to
            <input type="number" min="0" max="100" className="h-9 w-20 rounded-lg border border-[#D2E0D8] px-2" defaultValue={rules.rate_flex_pct ?? 30} disabled={role !== "admin"}
              onBlur={(e) => Number(e.target.value) !== rules.rate_flex_pct && setRule("rate_flex_pct", Math.max(0, Math.min(100, Number(e.target.value) || 0)))} data-testid="rule-rate-flex" />
            % above or below the standard rate (0 = any rate)</label>
          <p className="text-xs text-[#7B968B]">The admin can also override a single booking or assignment when asked.</p>
        </div>
      )}
      <p className="text-sm text-[#4A6358]">Check the documents uploaded by customers and pickup partners. Open each file, then approve or reject with a reason. Customers get a notification. Bookings and partner apps show "Pending verification" until approved.</p>
      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`kyc-filter-${f.toLowerCase()}`}
            className={`h-9 rounded-full px-4 text-sm font-semibold ${filter === f ? "bg-[#0D3B2B] text-white" : "border border-[#D2E0D8] bg-white"}`}>
            {f} ({f === "All" ? rows.length : rows.filter((r) => r.kyc.status === f).length})</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="admin-kyc-list">
        {shown.map((r) => (
          <div key={`${r.kind}-${r.id}`} className="flex flex-wrap items-start gap-4 rounded-2xl border border-[#D2E0D8] bg-white p-4" data-testid={`kyc-row-${r.id}`}>
            <div className="min-w-[220px] flex-1">
              <p className="font-bold text-[#0D3B2B]">{r.name} <span className="text-xs font-normal text-[#7B968B]">· {r.type} · {r.mobile}</span></p>
              <p className="text-sm">{r.kyc.doc_type}{r.kyc.doc_number ? ` · ${r.kyc.doc_number}` : ""}</p>
              <p className="text-xs text-[#7B968B]">Sent {r.kyc.submitted_at ? fmtDateTime(r.kyc.submitted_at) : "—"}{r.kyc.reviewed_by ? ` · reviewed by ${r.kyc.reviewed_by}` : ""}</p>
              {r.kyc.reason && <p className="text-xs text-[#991B1B]">Reason: {r.kyc.reason}</p>}
              <div className="mt-2 flex flex-wrap gap-2">
                {(r.kyc.files || []).map((f, i) => (
                  <a key={f.path} href={fileUrl(f.path)} target="_blank" rel="noreferrer" data-testid={`kyc-view-file-${r.id}-${i}`}
                    className="rounded-lg border border-[#2E9E6B] px-3 py-1 text-xs font-bold text-[#2E9E6B]">{f.content_type === "application/pdf" ? "PDF" : "Photo"} {i + 1}</a>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <KycBadge status={r.kyc.status} testid={`kyc-status-${r.id}`} />
              {r.kyc.status !== "Approved" && <button onClick={() => decide(r, "Approved")} className="h-9 rounded-xl bg-[#2E9E6B] px-4 text-sm font-bold text-white" data-testid={`kyc-approve-${r.id}`}>Approve</button>}
              {r.kyc.status !== "Rejected" && <button onClick={() => decide(r, "Rejected")} className="h-9 rounded-xl bg-[#E05C3A] px-4 text-sm font-bold text-white" data-testid={`kyc-reject-${r.id}`}>Reject</button>}
            </div>
          </div>
        ))}
        {!shown.length && <p className="rounded-2xl bg-white p-6 text-center text-[#7B968B]" data-testid="kyc-empty">Nothing here.</p>}
      </div>
    </div>
  );
};
